import {getBrowserSearchCompatibleEnv} from '@/shared/lib/env/getBrowserEnv';
import {getConfig} from '@/shared/lib/localStorage/internalAnalytics';
import {notNullValues} from '@/shared/lib/object';
import {logger} from '@/shared/services/logger';

import {ApiSuccessResponse, httpGet, httpPost} from '../serve';

const USER_DEVICE = {
  os: 'web',
};

type SignUpUser = {
  [key: string]: unknown;
};

type AuthResponse = undefined;
export type SuccessAuthResponse = ApiSuccessResponse<unknown>;

function getMetadata(): Record<string, string> {
  return notNullValues({
    uuid: getConfig()?.uuid,
  });
}

export const getDefaultAuthQueryParams = async () => {
  try {
    const browserEnv = await getBrowserSearchCompatibleEnv();
    return new URLSearchParams({
      ...browserEnv,
      rate_limit: '1',
    });
  } catch (error) {
    logger.critical('Error getting browser environment:', {
      error: String(error),
    });
    return new URLSearchParams();
  }
};

export const getRequiredAuthHeaders = () => {
  return {
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

export async function signUpViaGoogle(id_token: string, user: SignUpUser = {}) {
  const data = {
    flow: 'up',
    id_token,
    user_device: USER_DEVICE,
    ...getMetadata(),
    ...user,
  };

  const formData = new FormData();
  formData.append('content', JSON.stringify(data));

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<AuthResponse>(
    `/sign/in/google?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data;
}

export async function singUpViaEmail(user: SignUpUser) {
  const formData = new FormData();

  formData.append(
    'content',
    JSON.stringify({
      flow: 'up',
      user_device: USER_DEVICE,
      lang: 'en',
      ...getMetadata(),
      ...user,
    })
  );

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<AuthResponse>(
    `/sign/up?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
      withCredentials: true,
    }
  );

  return response.data;
}

export async function signUpViaApple(code: string, user: SignUpUser = {}) {
  const data = {
    flow: 'up',
    code_v2: code,
    user_device: USER_DEVICE,
    ...getMetadata(),
    ...user,
  };

  const formData = new FormData();
  formData.append('content', JSON.stringify(data));

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<AuthResponse>(
    `/sign/in/apple?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data;
}

export async function signInViaGoogle(id_token: string) {
  const data = {
    flow: 'in',
    id_token,
    user_device: USER_DEVICE,
  };

  const formData = new FormData();
  formData.append('content', JSON.stringify(data));

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<AuthResponse>(
    `/sign/in/google?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data;
}

export async function signInViaApple(code: string) {
  const data = {
    flow: 'in',
    code_v2: code,
    user_device: USER_DEVICE,
  };

  const formData = new FormData();
  formData.append('content', JSON.stringify(data));

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<AuthResponse>(
    `/sign/in/apple?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data;
}

export async function reachSignUpLimit() {
  const response = await httpGet<{last: boolean}>('/sign/rate_limit/check');
  return response.data.result;
}

export async function singUpViaPhone(userData: Record<string, unknown> = {}) {
  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<AuthResponse>(
    `/sign/in/phone?${params}`,
    {
      flow: 'up',
      user_device: USER_DEVICE,
      lang: 'en',
      dob: `${userData.day}.${userData.month}.${userData.year}`,
      ...userData,
      ...getMetadata(),
    },
    {
      headers: getRequiredAuthHeaders(),
      withCredentials: true,
    }
  );

  return response.data;
}

export async function signInViaPhone(phone: string) {
  const params = await getDefaultAuthQueryParams();
  params.append('lite', '1');

  const response = await httpPost<AuthResponse>(
    `/sign/in/phone?${params}`,
    {
      phone,
    },
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data;
}

export async function requestPhoneConfirmation() {
  const response = await httpPost('/phone_confirmation', undefined, {
    headers: getRequiredAuthHeaders(),
    withCredentials: true,
  });

  return response.data;
}

export async function confirmPhoneCode({
  phone,
  code,
}: {
  phone: string;
  code: string;
}) {
  let normalizedPhone = phone || '';
  if (!normalizedPhone.startsWith('+')) {
    normalizedPhone = `+${normalizedPhone}`;
  }

  const {data} = await httpPost(
    '/phone_confirmation',
    {
      phone: normalizedPhone,
      code,
    },
    {
      headers: getRequiredAuthHeaders(),
      withCredentials: true,
    }
  );

  return data;
}
